import * as React from 'react'

import { Box, Button } from '@factorialco/deprecated-design-system'
import { Reorder } from 'framer-motion'
import { DataTableProjection, ProjectionKind } from 'generated/resources/datatable.graphql'

import useProjections from 'modules/core/components/DataTable/hooks/useProjections'
import Sidepanel from 'modules/core/components/Sidepanel'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import i18n from 'modules/core/lib/i18n'

import Field from './Field'
import './index.styles.css'
import useSplittedProjections from './useSplittedProjections'

export type SortableProjection = {
  identifier: string
  title: string
  active?: boolean
  onToggle?: (value: boolean) => void
}

type Props = {
  onClose: () => void
  open: boolean
}

function EditColumnsModal<Paths, S extends string>({ onClose, open }: Props) {
  const { setProjections, initialProjections } = useProjections<Paths, S>()
  const [fixedTopProjections, shownProjections, hiddenProjections, fixedBottomProjections] =
    useSplittedProjections<Paths, S>()

  const [newProjections, setNewProjections] = React.useState([
    ...shownProjections,
    ...hiddenProjections,
  ])
  // NOTE: Using a Set here so we don't need iterate to check if an identifier is present
  const [newIdentifiers, setNewIdentifiers] = React.useState(
    new Set(shownProjections.map((p) => p.identifier))
  )

  function onSave() {
    const newProjectionsToSave = newProjections
      .filter((projection) => newIdentifiers.has(projection.identifier))
      .map((newProjection) => {
        const foundProjection = initialProjections.find(
          (projection) => projection.identifier === newProjection.identifier
        )

        if (!foundProjection) {
          throw new Error(`Invalid projection: ${newProjection.identifier}`)
        }

        return foundProjection
      })
    const projectionsToSave = [
      ...fixedTopProjections,
      ...newProjectionsToSave,
      ...fixedBottomProjections,
    ]

    setProjections(projectionsToSave)
    onClose()
  }

  function onToggle(projection: DataTableProjection<Paths, S>, value: boolean) {
    const newSet = new Set(newIdentifiers)
    if (value) {
      newSet.add(projection.identifier)
    } else {
      newSet.delete(projection.identifier)
    }
    setNewIdentifiers(newSet)
  }

  const topProjections = useUnsafeMemo(
    () => fixedTopProjections.filter((projection) => projection.kind !== ProjectionKind.Decorative),
    [fixedTopProjections]
  )
  const bottomProjections = useUnsafeMemo(
    () =>
      fixedBottomProjections.filter((projection) => projection.kind !== ProjectionKind.Decorative),
    [fixedBottomProjections]
  )

  return (
    <Sidepanel
      title={i18n.t('data_table.edit_columns.modal.title')}
      closeLabel={i18n.t('common.cancel')}
      onClose={onClose}
      open={open}
      overlay={false}
      footer={
        <>
          <Button type="secondary" onClick={onClose}>
            {i18n.t('common.cancel')}
          </Button>
          <Button type="primary" onClick={onSave}>
            {i18n.t('data_table.edit_columns.modal.save')}
          </Button>
        </>
      }
    >
      <Box flexDirection="column" gap="s16">
        {topProjections.length > 0 && (
          <div className={'EditColumnsModal_list'}>
            {topProjections.map((projection) => (
              <div className={'EditColumnsModal_item'} key={projection.identifier}>
                <Field projection={projection} sortable={false} active />
              </div>
            ))}
          </div>
        )}
        <Reorder.Group
          axis="y"
          values={newProjections}
          onReorder={setNewProjections}
          className={'EditColumnsModal_list'}
        >
          {newProjections.map((projection) => (
            <Reorder.Item
              key={projection.identifier}
              value={projection}
              className={`${'EditColumnsModal_item'} ${'EditColumnsModal_draggableItem'}`}
            >
              <Field
                projection={projection}
                onToggle={(value) => onToggle(projection, value)}
                active={newIdentifiers.has(projection.identifier)}
                sortable
              />
            </Reorder.Item>
          ))}
        </Reorder.Group>
        {bottomProjections.length > 0 && (
          <div className={'EditColumnsModal_list'}>
            {bottomProjections.map((projection) => (
              <div className={'EditColumnsModal_item'} key={projection.identifier}>
                <Field projection={projection} sortable={false} active />
              </div>
            ))}
          </div>
        )}
      </Box>
    </Sidepanel>
  )
}

export default EditColumnsModal
